import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccountStatus,
  BigAddContributionIcon,
  BigDisabledAddContributionIcon,
  Enrollment,
  Inscription,
  SmallAddContributionIcon,
} from '@common-fe/common-fe';

import { Roles } from '@/common/constants';
import ROUTES from '@/common/routes';
import { useHasAccess, useHistory } from '@/modules/core/hooks';
import { ErrorContributionModal } from '@/modules/transaction/components/Contributions/ContributionModals/ErrorContributionModal';
import { MakeContributionModal } from '@/modules/transaction/components/Contributions/ContributionModals/MakeContributionModal';
import { SuccessContributionModal } from '@/modules/transaction/components/Contributions/ContributionModals/SuccessContributionModal';

export interface FundManagementOption {
  title: string;
  description?: string | React.ReactNode;
  icon?: React.ReactNode;
  isWarningIcon?: boolean;
  externalButtonIcon?: React.ReactNode;
  onClick?: () => void;
  buttonText?: string;
  isButtonLoading?: boolean;
  isButtonDisabled?: boolean;
  additionalContent?: React.ReactNode;
  isWithHint?: boolean;
  hintContent?: React.ReactNode;

  children?: React.ReactNode | null;
}

interface Params {
  accountId?: string;
  isNotVerified?: boolean;
  isFullLiquidation?: boolean;
}
export const useFundManagementOptions = (myAccount?: Enrollment, params?: Params) => {
  const history = useHistory();
  const [showMakeContributionModal, setShowMakeContributionModal] = useState(false);
  const { t } = useTranslation();
  const currentAccountId = useMemo(() => (myAccount?.id ? `${myAccount?.id}` : undefined), [myAccount]);

  const isLimitedAccess = useHasAccess([
    {
      role: Roles.employeeLimitedAccess,
    },
  ]);

  const contributionAdditionalContent = useMemo(
    () => (
      <>
        <MakeContributionModal
          visible={showMakeContributionModal}
          onSetVisible={setShowMakeContributionModal}
          account={myAccount}
        />
        <SuccessContributionModal />
        <ErrorContributionModal />
      </>
    ),
    [showMakeContributionModal, myAccount]
  );

  const contributionHintContent = useMemo(() => {
    if (params?.isNotVerified) {
      return (
        <Inscription size="small" textAlign="center">
          {t('We need to verify your identity to establish your HSA.')}
        </Inscription>
      );
    }
    if (isLimitedAccess) {
      return (
        <Inscription size="small" textAlign="center">
          {t('Complete your setup to gain full access to your account.')}
        </Inscription>
      );
    }
    return '';
  }, [isLimitedAccess, params, t]);

  const isContributionButtonDisabled = useMemo(
    () =>
      !myAccount?.allowIndividualContributions ||
      myAccount?.accountStatus === AccountStatus.PENDING ||
      isLimitedAccess ||
      !!params?.isNotVerified,
    [myAccount, isLimitedAccess, params]
  );

  const contributionItem = useMemo(
    () => ({
      title: 'Contributions',
      description:
        isContributionButtonDisabled || params?.isNotVerified
          ? t('Unavailable for now')
          : t('Start saving money'),
      icon:
        isContributionButtonDisabled || params?.isNotVerified ? (
          <BigDisabledAddContributionIcon />
        ) : (
          <BigAddContributionIcon />
        ),
      externalButtonIcon: <SmallAddContributionIcon />,
      onClick: () => history.push(ROUTES.CONTRIBUTION(currentAccountId)),
      additionalContent: contributionAdditionalContent,
      buttonText: t('Make a Contribution'),
      isButtonDisabled: isContributionButtonDisabled,
      isWarningIcon: isContributionButtonDisabled && params?.isNotVerified,
      isWithHint: isLimitedAccess || !!params?.isNotVerified,
      hintContent: contributionHintContent,
    }),
    [
      contributionAdditionalContent,
      isContributionButtonDisabled,
      isLimitedAccess,
      t,
      params,
      contributionHintContent,
      history,
      currentAccountId,
    ]
  );

  const hideContributionItem = useMemo(() => !myAccount?.allowIndividualContributions, [myAccount]);

  const fundManagementItems = useMemo(
    () => [...(!hideContributionItem || params?.isNotVerified ? [contributionItem] : [])],
    [params, hideContributionItem, contributionItem]
  );

  const fundManagementButtons = useMemo(
    () => [...(!isContributionButtonDisabled && !hideContributionItem ? [contributionItem] : [])],
    [hideContributionItem, isContributionButtonDisabled, contributionItem]
  );

  return {
    fundManagementItems,
    fundManagementButtons,
  };
};
export default useFundManagementOptions;
